import { ApiService, type ApiRequestConfig } from "./api.service";
import type {
  INotificationSettingsResponse,
  IUser,
  IUserNotificationPreferences,
  UserProfile,
  UserSettings,
} from "@/types/user";

/** The caller's instance-scope standing: instance admin or not, plus the
 *  concrete actions their instance-scope grants carry (`instance.sales`,
 *  NG-1237). Neither depends on membership in any workspace. */
interface InstanceStanding {
  is_instance_admin: boolean;
  instance_permissions: string[];
}

/** Everything the web shell needs to boot, from `users/session/` (NG-1338).
 *
 *  `is_authenticated` is the only field a visitor without a session gets: the
 *  endpoint is `AllowAny`, so "logged out" arrives as a 200 with the other four
 *  absent, not as the 403 the `users/me/*` endpoints return. */
export interface ShellSession {
  is_authenticated: boolean;
  user?: IUser;
  profile?: UserProfile;
  settings?: UserSettings;
  instance?: InstanceStanding;
}

export class UserService extends ApiService {
  /** The shell's boot payload in one request: user, profile, workspace context
   *  and instance standing. It is the only read of them; the `users/me/*`
   *  endpoints below own their writes.
   *
   *  Cache-busted: the workspace context it carries changes as the user moves
   *  between workspaces, and a heuristically cached 200 would boot them into
   *  the one they left. */
  async session(): Promise<ShellSession> {
    return this.get(`/api/users/session/?t=${Date.now()}`);
  }

  async updateCurrentUser(data: Partial<IUser>): Promise<IUser> {
    return this.patch("/api/users/me/", data);
  }

  async updateCurrentUserProfile(data: Partial<UserProfile>): Promise<UserProfile> {
    return this.patch("/api/users/me/profile/", data);
  }

  async generateEmailChangeCode(email: string): Promise<{ code: string }> {
    return this.post("/api/users/me/email/generate-code/", { email });
  }

  async updateEmail(email: string, code: string): Promise<IUser> {
    return this.patch("/api/users/me/email/", { email, code });
  }

  async currentUserNotificationPreferences(): Promise<IUserNotificationPreferences> {
    return this.get("/api/users/me/notification-preferences/");
  }

  async updateCurrentUserNotificationPreferences(
    data: Partial<IUserNotificationPreferences>,
  ): Promise<IUserNotificationPreferences> {
    return this.patch("/api/users/me/notification-preferences/", data);
  }

  // Per-category notification settings (#1110). The data is per-user, but the
  // endpoint is workspace-scoped alongside the other notification routes.
  async notificationSettings(
    workspaceSlug: string,
    config?: ApiRequestConfig,
  ): Promise<INotificationSettingsResponse> {
    return this.get(`/api/workspaces/${workspaceSlug}/notification-settings/`, config);
  }

  async updateNotificationSettings(
    workspaceSlug: string,
    settings: Record<string, boolean>,
  ): Promise<INotificationSettingsResponse> {
    return this.patch(`/api/workspaces/${workspaceSlug}/notification-settings/`, { settings });
  }

  /** The **Teams** half of the same endpoint (NG-1187).
   *
   *  A sibling method rather than a second argument on the one above, because the two
   *  keys are independent switches and the endpoint treats them so: writing `teams`
   *  never moves `enabled`, and a method that took both would let a caller send the
   *  in-app value it happened to have in hand as though it were a decision.
   *
   *  `null` for a category is the reset — it clears the stored choice and hands the
   *  category back to the default set, which is **not** the same as `false` (the
   *  default set is on for some categories and off for others). No UI sends it yet;
   *  the type carries it because the contract does.
   */
  async updateTeamsNotificationSettings(
    workspaceSlug: string,
    teams: Record<string, boolean | null>,
  ): Promise<INotificationSettingsResponse> {
    return this.patch(`/api/workspaces/${workspaceSlug}/notification-settings/`, { teams });
  }
}
